import React, { useEffect, useState } from "react";
import { contentAdminApi } from "../../../lib/api";
import { SiteHeader, SiteTabs } from "./SiteDashboard";
import { PAGE_LABELS } from "./blockSchemas";
import { Loader2, Save, ChevronDown, ChevronUp } from "lucide-react";

export default function SEOEditor() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await contentAdminApi.listPages();
        setPages(res.data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const setField = (pageKey, key, value) => {
    setDrafts((d) => ({ ...d, [pageKey]: { ...(d[pageKey] || {}), [key]: value } }));
  };

  const save = async (page) => {
    const payload = drafts[page.page_key] || {};
    if (Object.keys(payload).length === 0) return;
    setSaving((s) => ({ ...s, [page.page_key]: true }));
    try {
      const res = await contentAdminApi.updateSEO(page.page_key, payload);
      setPages((prev) => prev.map((p) => p.page_key === page.page_key ? { ...p, ...res.data } : p));
      setDrafts((d) => { const next = { ...d }; delete next[page.page_key]; return next; });
      setNotice({ pageKey: page.page_key, msg: "SEO aggiornato." });
      setTimeout(() => setNotice(null), 3000);
    } catch (e) {
      setNotice({ pageKey: page.page_key, msg: e?.response?.data?.detail || "Errore", err: true });
    } finally {
      setSaving((s) => ({ ...s, [page.page_key]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <SiteTabs activeKey="seo" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Sito Web</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937]">SEO per pagina</h1>
          <p className="text-slate-500 mt-1 text-sm">Meta title, description, Open Graph e robots per ogni pagina pubblica.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            {pages.map((p) => {
              const draft = drafts[p.page_key] || {};
              const val = (k) => k in draft ? draft[k] : p[k];
              const isOpen = open === p.page_key;
              return (
                <div key={p.page_key} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`seo-card-${p.page_key}`}>
                  <button className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50"
                    onClick={() => setOpen(isOpen ? null : p.page_key)}>
                    <div className="text-left">
                      <div className="font-semibold text-slate-900">{PAGE_LABELS[p.page_key] || p.title || p.page_key}</div>
                      <div className="text-xs text-slate-500">
                        {p.seo_title ? <>Title: <span className="text-slate-700">{p.seo_title.substring(0, 60)}</span></> : <span className="text-slate-400">Nessun meta title configurato (userà default globale)</span>}
                      </div>
                    </div>
                    {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </button>
                  {isOpen && (
                    <div className="p-4 border-t border-slate-100 space-y-4">
                      {notice?.pageKey === p.page_key && (
                        <div className={`text-sm rounded-lg px-3 py-2 ${notice.err ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>{notice.msg}</div>
                      )}
                      <SEOField label="Meta Title" value={val("seo_title")} onChange={(v) => setField(p.page_key, "seo_title", v)} max={70} testid={`seo-title-${p.page_key}`} />
                      <SEOField label="Meta Description" value={val("seo_description")} onChange={(v) => setField(p.page_key, "seo_description", v)} rows={3} max={160} testid={`seo-description-${p.page_key}`} />
                      <SEOField label="OG Image URL" value={val("seo_og_image")} onChange={(v) => setField(p.page_key, "seo_og_image", v)} testid={`seo-og-${p.page_key}`} />
                      <SEOField label="Keywords" value={val("seo_keywords")} onChange={(v) => setField(p.page_key, "seo_keywords", v)} testid={`seo-keywords-${p.page_key}`} />
                      <SEOField label="Canonical URL" value={val("seo_canonical_url")} onChange={(v) => setField(p.page_key, "seo_canonical_url", v)} testid={`seo-canonical-${p.page_key}`} />
                      <div className="flex gap-6">
                        <label className="inline-flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={!!val("seo_noindex")} onChange={(e) => setField(p.page_key, "seo_noindex", e.target.checked)} />
                          noindex
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={!!val("seo_nofollow")} onChange={(e) => setField(p.page_key, "seo_nofollow", e.target.checked)} />
                          nofollow
                        </label>
                      </div>
                      <button
                        onClick={() => save(p)}
                        disabled={Object.keys(draft).length === 0 || saving[p.page_key]}
                        className="inline-flex items-center gap-1.5 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-40 text-white rounded-full px-4 py-2 text-sm"
                        data-testid={`seo-save-${p.page_key}`}
                      >
                        {saving[p.page_key] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Salva SEO
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SEOField({ label, value, onChange, rows, max, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 mb-1.5 block">
        {label}
        {max && <span className="text-xs text-slate-400 ml-2">{(value || "").length}/{max}</span>}
      </label>
      {rows ? (
        <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} maxLength={max}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={testid} />
      ) : (
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} maxLength={max}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={testid} />
      )}
    </div>
  );
}
